import type { SensorCurrentState } from '@jums-dashboard/shared';

export type Freshness = 'fresh' | 'stale' | 'unknown';

/**
 * Decides whether a sensor's `currentState` is fresh based on its
 * `staleAfterSeconds` threshold. Returns `'unknown'` when there is no
 * state at all.
 */
export function computeFreshness(state: SensorCurrentState | null, staleAfterSeconds: number, nowMs: number = Date.now()): Freshness {
  if (!state) return 'unknown';
  const observedMs = state.observedAt.toMillis();
  if (!Number.isFinite(observedMs)) return 'unknown';
  const ageSec = (nowMs - observedMs) / 1000;
  return ageSec <= staleAfterSeconds ? 'fresh' : 'stale';
}

// ─── Tiered health (fixed thresholds for the sensors tile badges) ────────────

export type SensorHealthTier = 'fresh' | 'aging' | 'stale' | 'unknown';

/** Reading older than this shows a yellow age badge. */
export const AGING_AFTER_MS = 2 * 60_000;
/** Reading older than this shows a red age badge. */
export const STALE_AFTER_MS = 60 * 60_000;
/** Below this battery voltage a "low batt" badge is shown. */
export const LOW_BATTERY_MV = 2500;

export interface SensorHealth {
  tier: SensorHealthTier;
  ageMs: number | null;
}

/**
 * Two-tier staleness with fixed thresholds (2 min → aging, 1 hr → stale),
 * independent of the per-sensor `staleAfterSeconds` used elsewhere.
 */
export function computeSensorHealth(state: SensorCurrentState | null, nowMs: number = Date.now()): SensorHealth {
  if (!state) return { tier: 'unknown', ageMs: null };
  const observedMs = state.observedAt.toMillis();
  if (!Number.isFinite(observedMs)) return { tier: 'unknown', ageMs: null };
  const ageMs = nowMs - observedMs;
  if (ageMs >= STALE_AFTER_MS) return { tier: 'stale', ageMs };
  if (ageMs >= AGING_AFTER_MS) return { tier: 'aging', ageMs };
  return { tier: 'fresh', ageMs };
}

export function isLowBattery(batteryMv: number | null | undefined): boolean {
  return typeof batteryMv === 'number' && Number.isFinite(batteryMv) && batteryMv > 0 && batteryMv < LOW_BATTERY_MV;
}

/** Compact humanized age for a badge, e.g. "45 s", "6 min", "3 h", "24 d". */
export function formatCompactAge(ms: number): string {
  const sec = Math.max(0, Math.round(ms / 1000));
  if (sec < 60) return `${sec} s`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min} min`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr} h`;
  const days = Math.round(hr / 24);
  return `${days} d`;
}
